import React from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { Skeleton } from '@mantine/core';

import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';

const columnHelper = createColumnHelper<Record<string, string>>();

type TableProps = {
  headers: string[];
  rows: string[][];
  isLoading: boolean;
};

/** Base component for {@link ChartType} 'table' and 'leaderboard-table' */
export function Table({ headers, rows, isLoading }: TableProps) {
  const { timezone } = useTimeRangeParams();

  const columns = React.useMemo(
    () =>
      headers.map((header) =>
        columnHelper.accessor(header, {
          header: () => header,
          cell: (info) => {
            if (isLoading) {
              return <Skeleton h="1rem" w="90%" />;
            }

            const value = info.getValue();

            // Format timestamp in selected timezone
            if (header === 'timestamp') {
              const dateTime = DateTime.fromISO(value).setZone(timezone);

              return dateTime.isValid
                ? dateTime.toLocaleString(DateTime.DATETIME_MED)
                : value;
            }

            return value;
          },
        })
      ),
    [headers, isLoading, timezone]
  );

  const tableData = React.useMemo(
    () =>
      isLoading
        ? Array(rows.length || 3).fill(
            Object.fromEntries(headers.map((header) => [header, null]))
          )
        : rows.map((row) =>
            Object.fromEntries(
              headers.map((header, index) => [header, row[index]])
            )
          ),
    [headers, rows, isLoading]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-auto">
      <table className="w-full divide-y divide-neutral-200 dark:divide-neutral-700">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="sticky top-0 z-5 px-3 pb-3 text-left text-xs font-medium tracking-wider uppercase first:pl-0 last:pr-0"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td
                colSpan={table.getHeaderGroups()[0].headers.length}
                className="py-3 text-center"
              >
                No data for this time period and filters.
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-3 py-3 text-xs first:pl-0 last:pr-0"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
